import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchDataAxios } from "../hooks/services/services";
import {
  getDefaultRoute,
  PERMISSIONS,
  getRequiredCodesForPrivilegeLabel,
  normalizePrivilegeLabel,
} from "../config/accessControl";
import { logger } from "../utils/logger";

const ACCESS_CONTEXT_STORAGE_KEY = "access_context";

const AccessContext = createContext(null);

const readStoredAccessContext = () => {
  const rawValue = localStorage.getItem(ACCESS_CONTEXT_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    localStorage.removeItem(ACCESS_CONTEXT_STORAGE_KEY);
    return null;
  }
};

const hasWildcardPermission = (permissions = []) =>
  permissions.includes(PERMISSIONS.ALL);

const normalizePermissionCode = (permission) => {
  if (!permission) return "";
  if (typeof permission === "string") return permission.trim().toUpperCase();
  if (typeof permission === "object" && permission.code) {
    return String(permission.code).trim().toUpperCase();
  }
  return String(permission).trim().toUpperCase();
};

export const AccessProvider = ({ children }) => {
  const [accessData, setAccessData] = useState(() => readStoredAccessContext());
  const [isAccessLoading, setIsAccessLoading] = useState(
    Boolean(localStorage.getItem("access_token"))
  );

  const clearAccessContext = useCallback(() => {
    setAccessData(null);
    localStorage.removeItem(ACCESS_CONTEXT_STORAGE_KEY);
    // Also clear the tokens themselves. Previously only the cached profile
    // (access_context) was removed here, which left a stale/invalid
    // access_token in localStorage. isAuthenticated is derived from
    // access_token's presence, so that token kept being read as "valid"
    // and re-attached to every subsequent request (e.g. the
    // user-app-state PUT), producing real 401s from the server even
    // though the client already knew the session was dead.
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  const persistAccessContext = useCallback((nextAccessData) => {
    setAccessData(nextAccessData);
    localStorage.setItem(
      ACCESS_CONTEXT_STORAGE_KEY,
      JSON.stringify(nextAccessData)
    );

    if (nextAccessData?.email) {
      localStorage.setItem("userEmail", nextAccessData.email);
    }

    if (nextAccessData?.username) {
      localStorage.setItem("userName", nextAccessData.username);
    }

    if (nextAccessData?.roleName) {
      localStorage.setItem("userRole", nextAccessData.roleName);
    }
  }, []);

  const refreshAccessContext = useCallback(async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      clearAccessContext();
      setIsAccessLoading(false);
      return null;
    }

    setIsAccessLoading(true);

    try {
      const response = await fetchDataAxios("/auth/me");
      const nextAccessData = response?.data || null;

      if (nextAccessData) {
        persistAccessContext(nextAccessData);
      } else {
        // /auth/me returned empty — token is present but invalid
        clearAccessContext();
      }

      return nextAccessData;
    } catch (error) {
      // 👇 Only logout on 401, not on every error.
      //
      // Note: services.js's axios response interceptor already attempts a
      // refresh (POST /auth/refresh-token, new token read from the
      // response header) and retries the original request once before a
      // 401 ever reaches this catch block. So if we're here with a 401,
      // the refresh has already been tried and failed — there's no need
      // (and no working second endpoint) to retry it again here.
      if (error?.response?.status === 401) {
        clearAccessContext();
      }
      // For non-401 errors (network, 500 etc), DON'T log out
      throw error;
    } finally {
      setIsAccessLoading(false);
    }
  }, [clearAccessContext, persistAccessContext]);

  useEffect(() => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      clearAccessContext();
      setIsAccessLoading(false);
      return;
    }

    refreshAccessContext().catch((error) => {
      // Silently ignore non-auth errors on mount so a bad network/server
      // response does not log the user out. The stored access_context in
      // localStorage keeps the session alive until a real 401 occurs.
      const status = error?.response?.status;
      if (status !== 401) {
        logger.warn("AccessContext refresh failed (non-401), keeping session:", status)
      }
      setIsAccessLoading(false);
    });
  }, [clearAccessContext, refreshAccessContext]);

  const permissions = useMemo(() => accessData?.permissions || [], [accessData]);
  const selectedPrivilegeGroups = useMemo(
    () => accessData?.selectedPrivilegeGroups || [],
    [accessData]
  );
  const scopeIds = useMemo(() => accessData?.scopeIds || [], [accessData]);
  const roleName = useMemo(
    () => accessData?.roleName || localStorage.getItem("userRole") || "",
    [accessData]
  );
  const username = useMemo(
    () =>
      accessData?.username ||
      localStorage.getItem("userName") ||
      "",
    [accessData]
  );
  const email = useMemo(
    () => accessData?.email || localStorage.getItem("userEmail") || "",
    [accessData]
  );
  const isAuthenticated = Boolean(localStorage.getItem("access_token"));
  const normalizedUserPermissionSet = useMemo(
    () =>
      new Set(
        (permissions || [])
          .map(normalizePermissionCode)
          .filter(Boolean)
      ),
    [permissions]
  );
  const selectedPrivilegeLabelSet = useMemo(
    () =>
      new Set(
        (selectedPrivilegeGroups || [])
          .map((group) => normalizePrivilegeLabel(group?.label || group))
          .filter(Boolean)
      ),
    [selectedPrivilegeGroups]
  );

  const hasPermission = useCallback(
    (requiredPermissions = []) => {
      const normalizedPermissions = Array.isArray(requiredPermissions)
        ? requiredPermissions.map(normalizePermissionCode).filter(Boolean)
        : [normalizePermissionCode(requiredPermissions)].filter(Boolean);

      if (normalizedPermissions.length === 0) {
        return true;
      }

      if (normalizedUserPermissionSet.has(PERMISSIONS.ALL)) {
        return true;
      }

      return normalizedPermissions.some((permission) =>
        normalizedUserPermissionSet.has(permission)
      );
    },
    [normalizedUserPermissionSet]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions = []) => {
      const normalizedPermissions = Array.isArray(requiredPermissions)
        ? requiredPermissions.map(normalizePermissionCode).filter(Boolean)
        : [normalizePermissionCode(requiredPermissions)].filter(Boolean);

      if (normalizedPermissions.length === 0) {
        return true;
      }

      if (normalizedUserPermissionSet.has(PERMISSIONS.ALL)) {
        return true;
      }

      return normalizedPermissions.every((permission) =>
        normalizedUserPermissionSet.has(permission)
      );
    },
    [normalizedUserPermissionSet]
  );

  const canAccessPrivilegeLabel = useCallback(
    (labels = []) => {
      const resolvedLabels = Array.isArray(labels) ? labels : [labels];
      const normalizedLabels = resolvedLabels
        .map((label) => normalizePrivilegeLabel(label))
        .filter(Boolean);

      if (normalizedLabels.length === 0) {
        return true;
      }

      const hasSelectedPrivilegeContext = selectedPrivilegeLabelSet.size > 0;
      if (hasSelectedPrivilegeContext) {
        return normalizedLabels.some((label) => {
          if (!selectedPrivilegeLabelSet.has(label)) {
            return false;
          }

          const requiredCodes = getRequiredCodesForPrivilegeLabel(label);
          return requiredCodes.length === 0 || hasAllPermissions(requiredCodes);
        });
      }

      return normalizedLabels.some((label) => {
        const requiredCodes = getRequiredCodesForPrivilegeLabel(label);
        return requiredCodes.length > 0 && hasAllPermissions(requiredCodes);
      });
    },
    [hasAllPermissions, selectedPrivilegeLabelSet]
  );

  const isInScope = useCallback(
    (scopeId) => {
      if (!scopeId) {
        return true;
      }

      if (hasWildcardPermission(permissions) || scopeIds.length === 0) {
        return true;
      }

      return scopeIds.includes(scopeId);
    },
    [permissions, scopeIds]
  );

  const filterSubProgramsByScope = useCallback(
    (subPrograms = []) => {
      if (!Array.isArray(subPrograms) || subPrograms.length === 0) {
        return [];
      }

      if (hasWildcardPermission(permissions) || scopeIds.length === 0) {
        return subPrograms;
      }

      const hasScopeAwareIds = subPrograms.some((subProgram) => subProgram?.id);

      if (!hasScopeAwareIds) {
        return subPrograms;
      }

      return subPrograms.filter((subProgram) => isInScope(subProgram?.id));
    },
    [isInScope, permissions, scopeIds]
  );

  const filterProgramsByScope = useCallback(
    (programs = []) => {
      if (!Array.isArray(programs) || programs.length === 0) {
        return [];
      }

      if (hasWildcardPermission(permissions) || scopeIds.length === 0) {
        return programs;
      }

      const hasScopeAwareIds = programs.some(
        (program) =>
          program?.id ||
          program?.subPrograms?.some((subProgram) => subProgram?.id)
      );

      if (!hasScopeAwareIds) {
        return programs;
      }

      return programs
        .filter((program) => {
          if (isInScope(program?.id)) {
            return true;
          }

          return (program?.subPrograms || []).some((subProgram) =>
            isInScope(subProgram?.id)
          );
        })
        .map((program) => {
          if (isInScope(program?.id)) {
            return program;
          }

          return {
            ...program,
            subPrograms: filterSubProgramsByScope(program?.subPrograms || []),
          };
        });
    },
    [filterSubProgramsByScope, isInScope, permissions, scopeIds]
  );

  const getUserDefaultRoute = useCallback(
    (nextAccessData = accessData) =>
      getDefaultRoute({
        permissions: nextAccessData?.permissions || [],
        selectedPrivilegeGroups: nextAccessData?.selectedPrivilegeGroups || [],
        roleName: nextAccessData?.roleName || roleName,
      }),
    [accessData, roleName]
  );

  const value = useMemo(
    () => ({
      accessData,
      permissions,
      selectedPrivilegeGroups,
      scopeIds,
      roleName,
      username,
      email,
      isAuthenticated,
      isAccessLoading,
      hasPermission,
      hasAllPermissions,
      canAccessPrivilegeLabel,
      isInScope,
      filterProgramsByScope,
      filterSubProgramsByScope,
      refreshAccessContext,
      clearAccessContext,
      getDefaultRoute: getUserDefaultRoute,
    }),
    [
      accessData,
      permissions,
      selectedPrivilegeGroups,
      scopeIds,
      roleName,
      username,
      email,
      isAuthenticated,
      isAccessLoading,
      hasPermission,
      hasAllPermissions,
      canAccessPrivilegeLabel,
      isInScope,
      filterProgramsByScope,
      filterSubProgramsByScope,
      refreshAccessContext,
      clearAccessContext,
      getUserDefaultRoute,
    ]
  );

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);

  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }

  return context;
};